import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  "TCF Score ≥ 60",
  "Controlli per ogni processo",
  "Almeno 1 test ToD per processo",
  "Strategia fiscale firmata",
  "Certificatore assegnato",
  "Report generato",
  "Certificazione emessa",
];

const QUALIFICATIONS = ["dottore_commercialista", "avvocato_tributarista", "revisore_legale", "consulente_lavoro"];

export default function Certification() {
  const [cert, setCert] = useState(null);
  const [list, setList] = useState([]);
  const [k, setK] = useState(null);
  const [form, setForm] = useState({
    certification_year: new Date().getFullYear(),
    certifier_name: "", certifier_vat: "", certifier_email: "",
    qualification: "dottore_commercialista", register_number: "",
    methodology: "", conclusions: "", final_judgement: "",
  });

  useEffect(() => {
    api.get("/certifications").then(({ data }) => { setList(data); setCert(data[0] || null); if (data[0]) setForm((f) => ({ ...f, ...data[0] })); });
    api.get("/dashboard/kpis").then(({ data }) => setK(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      if (cert) await api.put(`/certifications/${cert.id}`, form);
      else await api.post("/certifications", form);
      toast.success("Salvato");
      const { data } = await api.get("/certifications"); setList(data); setCert(data[0]);
    } catch (e) { toast.error("Errore"); }
  };

  const completed = (i) => {
    if (!k) return false;
    if (i === 0) return k.tcf_score >= 60;
    if (i === 1) return k.controls.total > 0;
    if (i === 2) return k.tests.executed > 0;
    if (i === 3) return !!cert;
    if (i === 4) return !!form.certifier_name;
    if (i === 5) return !!form.report_url;
    if (i === 6) return cert?.status === "certificato";
    return false;
  };

  return (
    <div className="space-y-6" data-testid="cert-page">
      <div>
        <div className="label-tag">Cooperative Compliance</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Certificazione TCF</h1>
      </div>

      <Card className="card-flat p-6">
        <div className="label-tag mb-4">Percorso di certificazione</div>
        <div className="space-y-2">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100" data-testid={`cert-step-${i}`}>
              <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${completed(i) ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                {completed(i) ? <Check size={14} /> : i + 1}
              </div>
              <div className={`text-sm ${completed(i) ? "text-slate-900 font-medium" : "text-slate-500"}`}>{s}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="card-flat p-6">
        <div className="label-tag mb-4">Dati certificatore</div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="label-tag">Nome certificatore</Label><Input value={form.certifier_name} onChange={(e) => setForm((f) => ({ ...f, certifier_name: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div><Label className="label-tag">P. IVA</Label><Input value={form.certifier_vat} onChange={(e) => setForm((f) => ({ ...f, certifier_vat: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div><Label className="label-tag">Email</Label><Input value={form.certifier_email} onChange={(e) => setForm((f) => ({ ...f, certifier_email: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div>
            <Label className="label-tag">Qualifica</Label>
            <Select value={form.qualification} onValueChange={(v) => setForm((f) => ({ ...f, qualification: v }))}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{QUALIFICATIONS.map((q) => <SelectItem key={q} value={q}>{q.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="label-tag">N° iscrizione albo</Label><Input value={form.register_number} onChange={(e) => setForm((f) => ({ ...f, register_number: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div><Label className="label-tag">Anno certificazione</Label><Input type="number" value={form.certification_year} onChange={(e) => setForm((f) => ({ ...f, certification_year: parseInt(e.target.value) }))} className="rounded-sm mt-1" /></div>
        </div>
        <div className="mt-4 space-y-3">
          <div><Label className="label-tag">Metodologia</Label><Textarea value={form.methodology} onChange={(e) => setForm((f) => ({ ...f, methodology: e.target.value }))} rows={3} className="rounded-sm mt-1" /></div>
          <div><Label className="label-tag">Conclusioni</Label><Textarea value={form.conclusions} onChange={(e) => setForm((f) => ({ ...f, conclusions: e.target.value }))} rows={3} className="rounded-sm mt-1" /></div>
          <div>
            <Label className="label-tag">Giudizio finale</Label>
            <Select value={form.final_judgement || ""} onValueChange={(v) => setForm((f) => ({ ...f, final_judgement: v }))}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent><SelectItem value="positivo">Positivo</SelectItem><SelectItem value="con_riserva">Con riserva</SelectItem><SelectItem value="negativo">Negativo</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-6"><Button onClick={save} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-cert">Salva certificazione</Button></div>
      </Card>
    </div>
  );
}
